import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Animation } from '@travel/ui';
import { isEmptyArray } from '@travel/utils';

import Pagination from 'favoriteList/components/Pagination';
import { ServiceType } from 'favoriteList/constants/favoriteLists';
import { getFilterObject, getQueryString } from 'favoriteList/constants/filters';
import paths from 'favoriteList/core/universalRouter/paths';
import useActionTimers from 'favoriteList/hooks/useActionTimers';
import { pushLocation } from 'favoriteList/store/__router/actions';
import {
  addFavoriteToProvider,
  fetchMoreProviderList,
  fetchProviderList,
  removeFavoriteToProvider,
} from 'favoriteList/store/providerPlan/actions';
import {
  getIsProviderFetching,
  getIsProviderFetchingMore,
  getProviderDataCount,
  getProviderList,
} from 'favoriteList/store/providerPlan/selectors';
import AccommodationPlanSkeleton from '../AccommodationPlanSkeleton';
import EmptyMessage from '../EmptyMessage';
import Popup from '../Popup';
import ProviderCard from '../ProviderCard';

import type { ProviderWithArrayIndex } from 'Provider-Types';
import type { Query } from 'ProviderPlan-Types';
import { getRequestFilter, getRequestSort } from '../../resolver';

import { MOBILE_SCREEN, useDeviceType } from '@travel/traveler-core/hooks';
import { DEFAULT_LIMIT_PROVIDER } from 'favoriteList/constants/defaultValue';
import styles from './providerList.module.scss';

type Props = {
  query: Query;
  isDomestic: boolean;
  isPlanList: boolean;
};

const ProviderList = (props: Props) => {
  const { query, isDomestic, isPlanList } = props;
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const isSP = deviceType === MOBILE_SCREEN;
  const [removeItemCount, setRemoveItemCount] = useState(0);

  const providerResponse = useSelector(getProviderList);
  const { body, status, statusMsg } = providerResponse;
  const offset = body?.offset || 0;
  const limit = body?.limit || DEFAULT_LIMIT_PROVIDER;
  const isProviderFetching = useSelector(getIsProviderFetching);
  const isProviderFetchingMore = useSelector(getIsProviderFetchingMore);
  const total = useSelector(getProviderDataCount);
  const isTotalDataEmpty = total === 0;
  const isCurrentPageDataEmpty = total - offset === 0;

  const fadeProviderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideProviderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshProviderList = () => {
    if (!isSP) {
      if (isCurrentPageDataEmpty) {
        const newOffset = Math.max(0, offset - limit);
        const path = isDomestic ? paths.domesticProvider.path : paths.overseasProvider.path;
        const queryString = getQueryString(query.f_sort || '', getFilterObject(query), newOffset);

        dispatch(
          pushLocation({
            pathname: path,
            search: queryString,
          }),
        );
      } else {
        dispatch(
          fetchProviderList({
            serviceType: isDomestic ? ServiceType.DOMESTIC : ServiceType.OVERSEAS_HOTEL,
            offset: offset,
            limit: limit,
            adultNum: 1,
            roomNum: 1,
            filters: getRequestFilter(query),
            sortOrder: getRequestSort(true, query),
          }),
        );
      }
    }
  };

  const { isFading, isOpen, initializePopUpState, showPopUp } = useActionTimers({
    fadeTimer: fadeProviderTimerRef,
    hideTimer: hideProviderTimerRef,
    endTimerHandler: refreshProviderList,
  });

  const onLoadNextPage = () => {
    dispatch(
      fetchMoreProviderList({
        serviceType: isDomestic ? ServiceType.DOMESTIC : ServiceType.OVERSEAS_HOTEL,
        offset: offset + limit - removeItemCount,
        limit: limit,
        adultNum: 1,
        roomNum: 1,
        filters: getRequestFilter(query),
        sortOrder: getRequestSort(true, query),
      }),
    );
    setRemoveItemCount(0);
  };

  const onClickPaginationButton = (offset: number, _limit: number) => {
    const path = isDomestic ? paths.domesticProvider.path : paths.overseasProvider.path;
    const queryString = getQueryString(query.f_sort || '', getFilterObject(query), offset);

    dispatch(
      pushLocation({
        pathname: path,
        search: queryString,
      }),
    );
  };

  const handleProviderFavoriteButtonClick = useCallback(
    async (provider: ProviderWithArrayIndex) => {
      initializePopUpState();
      await dispatch(removeFavoriteToProvider(provider));
      showPopUp();
      setRemoveItemCount((prev) => prev + 1);
    },
    [initializePopUpState, showPopUp, dispatch],
  );
  const isEmptyProviderList = isEmptyArray(body?.providers);

  const handleFavoriteRollbackClick = useCallback(async () => {
    initializePopUpState();
    await dispatch(addFavoriteToProvider(isDomestic)).catch(showPopUp);
    setRemoveItemCount((prev) => prev - 1);
  }, [initializePopUpState, dispatch, isDomestic, showPopUp]);

  return (
    <>
      {isProviderFetching || (isCurrentPageDataEmpty && isOpen && !isSP && !isTotalDataEmpty) ? (
        <AccommodationPlanSkeleton skeletonItemCount={4} isPlanList={isPlanList} />
      ) : isEmptyProviderList ? (
        <EmptyMessage isPlanList={isPlanList} status={status} statusMsg={statusMsg} />
      ) : (
        <div className={styles.providerCardContainer}>
          {body?.providers?.map((providerCardList, index) => {
            const providerWithIndex = { ...providerCardList, arrayIndex: index };
            return (
              <ProviderCard
                key={providerCardList.hotelNo}
                provider={providerWithIndex}
                handleProviderFavoriteButtonClick={handleProviderFavoriteButtonClick}
                serviceType={isDomestic ? ServiceType.DOMESTIC : ServiceType.OVERSEAS_HOTEL}
              />
            );
          })}
        </div>
      )}

      {total > 0 ? (
        <Pagination
          offset={body?.offset}
          total={total}
          limit={body?.limit}
          onClickPaginationButton={onClickPaginationButton}
          onLoadNextPage={onLoadNextPage}
          isFetching={isProviderFetchingMore}
          isPlanList={isPlanList}
        />
      ) : null}

      <Animation isStartAction={isOpen} type="fadeIn" animationDuration="short">
        <Popup
          isFade={isFading}
          clickHandler={handleFavoriteRollbackClick}
          label="お気に入りから削除しました。"
        />
      </Animation>
    </>
  );
};

export default ProviderList;
